import { useQueries, useQueryClient } from '@tanstack/react-query';
import { TaskItem } from '@demind-inc/core';

import { tasksApi } from '../api';
import { TaskFilterOptions, TasksByDate } from '../types';
import { TASK_FILTER_ALL_LABEL_KEY } from '../constants';
import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { taskFilterAtom } from '../recoil';

type TodoTaskFilter = Pick<TaskFilterOptions, 'onlyMyTask' | 'labelNames'>;
interface UseTodoTasksParams {
  todoTasksBoardIds: string[];
  dueDate: string;
  filter: TodoTaskFilter;
}

interface UseTodoTasksByDatesParams {
  todoTasksBoardIds: string[];
  dates: string[];
  filter?: TodoTaskFilter;
}

interface UseTodoTasksQueryParams {
  queryKey: ['lifestack.todo.tasks', UseTodoTasksParams];
}

export const useTodoTasks = ({ todoTasksBoardIds, dates, filter }: UseTodoTasksByDatesParams) => {
  const todoTasksQueries = useQueries({
    /* @ts-ignore */
    queries: dates.map((dueDate) => ({
      queryKey: ['lifestack.todo.tasks', { todoTasksBoardIds, dueDate, filter }],
      queryFn: ({ queryKey }: UseTodoTasksQueryParams) => {
        const [, { todoTasksBoardIds, dueDate, filter }] = queryKey;

        const transformedLabelNames =
          filter?.labelNames === undefined
            ? undefined
            : filter?.labelNames === TASK_FILTER_ALL_LABEL_KEY
              ? undefined
              : filter?.labelNames.join(',');

        return tasksApi
          .getTodoTasks(
            todoTasksBoardIds,
            dueDate,
            filter?.onlyMyTask,
            transformedLabelNames,
            false,
            true
          )
          .then(
            ({ data }) =>
              ({
                date: dueDate,
                tasks: (data ?? []) as TaskItem[],
                updatedAt: new Date().toISOString(),
              }) as TasksByDate
          );
      },
      refetchOnWindowFocus: true,
      enabled: !!todoTasksBoardIds.length,
      staleTime: 15000, // 15s
    })),
  });

  const loadingStatesByDate = dates.reduce(
    (acc, date, index) => {
      acc[date] = todoTasksQueries[index]?.isLoading ?? false;
      return acc;
    },
    {} as Record<string, boolean>
  );

  const isDateLoading = useCallback(
    (date: string) => {
      return loadingStatesByDate[date] ?? false;
    },
    [loadingStatesByDate]
  );

  return {
    todoTasksByDate: todoTasksQueries.map((query) => query.data ?? []).flat(),
    isLoading: todoTasksQueries.some((query) => query.isLoading),
    isDateLoading,
    ...todoTasksQueries,
  };
};

export const useTodoTasksCache = () => {
  const queryClient = useQueryClient();
  const taskFilter = useRecoilValue(taskFilterAtom);

  const findInCache = useCallback(
    ({
      todoTasksBoardIds,
      dueDate,
      filter,
    }: {
      todoTasksBoardIds: string[];
      dueDate: string;
      filter?: TodoTaskFilter;
    }) => {
      const tasks = queryClient.getQueryData<TasksByDate[]>([
        'lifestack.todo.tasks',
        { todoTasksBoardIds, dueDate, filter },
      ]);

      return tasks ? tasks.filter((item) => !!item) : [];
    },
    [queryClient]
  );

  const findInCacheByDate = useCallback(
    (dueDate: string, projectId?: string) => {
      // Get all queries from the cache
      const queries = queryClient.getQueriesData<TasksByDate>({
        queryKey: ['lifestack.todo.tasks'],
      });

      // Find all matching queries for the given date
      const matchingQueries = queries
        .filter(([queryKey]) => {
          const [_, params] = queryKey as ['lifestack.todo.tasks', UseTodoTasksParams];
          return (
            params.dueDate === dueDate &&
            params.todoTasksBoardIds.length > 0 &&
            (projectId ? params.todoTasksBoardIds.includes(projectId) : true) &&
            params.filter?.labelNames === taskFilter.labelNames &&
            params.filter?.onlyMyTask === taskFilter.onlyMyTask
          );
        })
        .map(([_, data]) => data);

      // If no matching queries found, return empty array
      if (matchingQueries.length === 0) {
        return [];
      }

      // Get the most recent query data based on updatedAt timestamp
      const latestData = matchingQueries.reduce((latest, current) => {
        if (!latest?.updatedAt) return current?.updatedAt ? current : null;
        if (!current?.updatedAt) return latest;

        return new Date(current.updatedAt) > new Date(latest.updatedAt) ? current : latest;
      }, null);

      return latestData?.tasks?.filter((item) => !!item) ?? [];
    },
    [queryClient, taskFilter]
  );

  /** Flatten every cached todo-tasks query into TaskItem[] — used by v4
   * result_json task adjustments to resolve before/taskFrom for update/remove. */
  const findAllInCache = useCallback((): TaskItem[] => {
    const queries = queryClient.getQueriesData<TasksByDate | TasksByDate[]>({
      queryKey: ['lifestack.todo.tasks'],
    });
    const byId = new Map<string, TaskItem>();
    queries.forEach(([, data]) => {
      const days = Array.isArray(data) ? data : data ? [data] : [];
      days.forEach((day) => {
        (day?.tasks ?? []).forEach((task) => {
          if (task?.taskId) {
            byId.set(task.taskId, task);
          }
        });
      });
    });
    return Array.from(byId.values());
  }, [queryClient]);

  return { findInCache, findInCacheByDate, findAllInCache };
};
